import React, { useEffect, useRef } from 'react';
import { Cpu, Brain, Bot, Zap, Code, Lightbulb, Network, Shield } from 'lucide-react';

const About: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Section reveal observer
    const sectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('reveal-active');
          sectionObserver.unobserve(entry.target);
        }
      },
      {
        root: null,
        threshold: 0.05, // Trigger earlier
        rootMargin: '0px 0px -100px 0px', // Trigger ~100px before it appears fully
      }
    );

    // Individual card reveal observer
    const cardObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const target = entry.target as HTMLElement;
            target.classList.add('reveal-active');
            cardObserver.unobserve(entry.target);
          }
        });
      },
      {
        root: null,
        threshold: 0.05, // Trigger earlier
        rootMargin: '0px 0px -100px 0px', // Reveal cards 100px early
      }
    );
  
  
    if (sectionRef.current) {
      sectionObserver.observe(sectionRef.current);
    }
  
    const cards = document.querySelectorAll('.about-card');
    cards.forEach((card, index) => {
      card.classList.add(`delay-${index * 150}`);
      cardObserver.observe(card);
    });
  
    return () => {
      sectionObserver.disconnect();
      cardObserver.disconnect();
    };
  }, []);

  const featureCards = [
    {
      icon: <Brain className="h-8 w-8 text-purple-400" />,
      title: 'Generative AI',
      description: 'Building scalable GenAI applications using LangChain, RAG pipelines, and advanced language models.',
    },
    {
      icon: <Bot className="h-8 w-8 text-blue-400" />,
      title: 'Agentic AI Systems',
      description: 'Designing multi-agent architectures for autonomous trading, data analysis, and intelligent automation.',
    },
    {
      icon: <Network className="h-8 w-8 text-indigo-400" />,
      title: 'Machine Learning',
      description: 'Implementing supervised and unsupervised learning models with TensorFlow, Scikit-learn, and deep learning frameworks.',
    },
    {
      icon: <Cpu className="h-8 w-8 text-purple-400" />,
      title: 'Data Science',
      description: 'Processing and analyzing large datasets using NumPy, Pandas, and advanced data preprocessing techniques.',
    },
    {
      icon: <Code className="h-8 w-8 text-blue-400" />,
      title: 'Python Development',
      description: 'Expert in Python programming for AI/ML applications, API development, and system automation.',
    },
    {
      icon: <Lightbulb className="h-8 w-8 text-indigo-400" />,
      title: 'AI Integration',
      description: 'Integrating AI capabilities into web applications and building intelligent user interfaces.',
    },
    {
      icon: <Zap className="h-8 w-8 text-purple-400" />,
      title: 'Real-time Systems',
      description: 'Building real-time AI systems for trading, sentiment analysis, and dynamic decision making.',
    },
    {
      icon: <Shield className="h-8 w-8 text-blue-400" />,
      title: 'Research & Innovation',
      description: 'Staying current with latest AI research and implementing cutting-edge solutions for business problems.',
    },
  ];

  return (
    <section 
      id="about" 
      ref={sectionRef}
      className="relative py-24 bg-gradient-to-b from-black to-purple-950/10 overflow-hidden reveal-section"
    >
      {/* Parallax background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-500/5 rounded-full filter blur-3xl transform -translate-x-1/2 -translate-y-1/2 animate-float"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full filter blur-3xl animate-float-delay"></div>
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold font-orbitron mb-4 tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-500">
            About Me
          </h2>
          <div className="h-1 w-20 bg-gradient-to-r from-purple-500 to-blue-500 mx-auto rounded-full mb-8"></div>
          <p className="max-w-3xl mx-auto text-lg text-gray-300">
            Passionate software engineer with a strong foundation in AI, machine learning, and distributed systems. 
            Experienced in building scalable Generative AI applications using Python, LangChain, and RAG pipelines. 
            Focused on solving real-world business problems and creating intelligent systems that deliver measurable impact.
          </p>
        </div>

        <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {featureCards.map((card, index) => (
            <div
              key={index}
              className="about-card relative group overflow-hidden rounded-xl bg-gradient-to-br from-gray-900 to-black p-0.5 reveal-card"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 to-blue-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative h-full bg-gray-950 rounded-[10px] p-6 flex flex-col">
                <div className="rounded-full bg-gray-800 w-16 h-16 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  {card.icon}
                </div>
                <h3 className="text-xl font-semibold font-orbitron mb-2 text-white">
                  {card.title}
                </h3>
                <p className="text-gray-400 flex-grow">
                  {card.description}
                </p>
                <div className="h-1 w-12 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full mt-4 transform origin-left scale-0 group-hover:scale-100 transition-transform duration-300"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;